import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { InitializeUpgradePaymentResponse } from '@upright/common/model/input/companies/api-types';
import { GetSubscriptionResponse } from '@upright/common/subscription/api-types';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import { getOrganizationOfUser } from '@upright/common/iam/organizations/common';

type UpgradeStep = 'closed' | 'tier-selection' | 'stripe-checkout' | 'confirming-payment' | 'success' | 'error';

interface UseUpgradeFlowParams {
  companyId: string;
  /** When false, the hook stays inert — URL params are ignored and all actions are no-ops. */
  enabled: boolean;
}

interface UseUpgradeFlowReturn {
  step: UpgradeStep;
  /** True when payment confirmation polling timed out — webhook hasn't fired yet */
  paymentConfirmationTimedOut: boolean;
  /** Error message when checkout initialization fails */
  checkoutError: string | null;
  openTierSelection: () => void;
  selectPro: () => void;
  closeTierSelection: () => void;
  closeCheckout: () => void;
  closeSuccess: () => void;
  closeError: () => void;
  fetchUpgradeClientSecret: () => Promise<string>;
}

function invalidateSubscriptionCaches(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['get-company-subscription'] });
  void queryClient.invalidateQueries({ queryKey: ['get-active-subscription'] });
  void queryClient.invalidateQueries({ queryKey: ['get-subscription'] });
}

export function useUpgradeFlow({ companyId, enabled }: UseUpgradeFlowParams): UseUpgradeFlowReturn {
  const [step, setStep] = useState<UpgradeStep>('closed');
  const [paymentConfirmationTimedOut, setPaymentConfirmationTimedOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const user = useGetLoggedInUser();
  const organization = user ? getOrganizationOfUser(user) : undefined;
  const organizationId = organization?.id;

  // Handle return from Stripe checkout (session_id + upgrade=true in URL).
  // Use a ref to ensure we only process the return params once, avoiding
  // re-triggers when setSearchParams updates the searchParams object.
  const sessionId = searchParams.get('session_id');
  const isUpgradeReturn = searchParams.get('upgrade') === 'true';
  const hasHandledReturn = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (sessionId && isUpgradeReturn && !hasHandledReturn.current) {
      hasHandledReturn.current = true;
      // Enter confirming-payment step — poll until webhook confirms tier change
      setStep('confirming-payment');
      setPaymentConfirmationTimedOut(false);
      // Clear the URL params
      const next = new URLSearchParams(searchParams);
      next.delete('session_id');
      next.delete('company_id');
      next.delete('upgrade');
      setSearchParams(next, { replace: true });
    }
  }, [sessionId, isUpgradeReturn, enabled, searchParams, setSearchParams]);

  // Poll for subscription tier change while confirming-payment step is active.
  // The Stripe webhook that completes the payment record is async — it may not have
  // fired by the time the user returns from Stripe checkout. This effect polls the
  // company subscription endpoint until the tier reflects 'pro', then transitions
  // to the success step.
  useEffect(() => {
    if (step !== 'confirming-payment' || !companyId) return;

    // Use an object so the `cancelled` flag is mutable across async boundaries
    // without triggering eslint's no-unnecessary-condition rule.
    const state = { cancelled: false };
    const POLL_INTERVAL_MS = 2000;
    const MAX_DURATION_MS = 30000;

    const poll = async () => {
      const startTime = Date.now();
      while (!state.cancelled && Date.now() - startTime < MAX_DURATION_MS) {
        try {
          const response = await fetchOrThrow<GetSubscriptionResponse>(`/subscription/company/${companyId}`, 'GET');
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated asynchronously by cleanup
          if (!state.cancelled && response.subscription.tier === 'pro') {
            // Webhook has processed — refresh all subscription caches and show success
            invalidateSubscriptionCaches(queryClient);
            setStep('success');
            return;
          }
        } catch (error) {
          console.warn('[upgrade-flow] Error polling subscription tier:', error);
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated asynchronously by cleanup
        if (state.cancelled) return;
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      }
      // Polling timed out — webhook hasn't fired yet
      if (!state.cancelled) {
        setPaymentConfirmationTimedOut(true);
      }
    };

    void poll();

    return () => {
      state.cancelled = true;
    };
  }, [step, companyId, queryClient]);

  const openTierSelection = useCallback(() => {
    setStep('tier-selection');
  }, []);

  const selectPro = useCallback(() => {
    setStep('stripe-checkout');
    setCheckoutError(null);
  }, []);

  const closeTierSelection = useCallback(() => {
    setStep('closed');
  }, []);

  const closeCheckout = useCallback(() => {
    setStep('closed');
    setCheckoutError(null);
  }, []);

  const closeSuccess = useCallback(() => {
    setStep('closed');
    // Invalidate subscription queries to refresh the tier display.
    invalidateSubscriptionCaches(queryClient);
  }, [queryClient]);

  const closeError = useCallback(() => {
    setStep('closed');
    setCheckoutError(null);
  }, []);

  const fetchUpgradeClientSecret = useCallback(async (): Promise<string> => {
    if (!organizationId) {
      const errorMessage = 'No organization ID available. Please try again or contact support.';
      setCheckoutError(errorMessage);
      setStep('error');
      throw new Error(errorMessage);
    }
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      const result = await fetchOrThrow<InitializeUpgradePaymentResponse>(
        `/organizations/${organizationId}/companies/${companyId}/payment/upgrade`,
        'POST',
        { returnUrl },
      );
      if (result.stripeClientSecret === null) {
        // Stripe session is already complete (payment went through but webhook hasn't fired yet).
        // Transition to confirming-payment to poll for the webhook instead of showing checkout.
        setStep('confirming-payment');
        setPaymentConfirmationTimedOut(false);
        throw new Error('Payment already completed — awaiting confirmation.');
      }
      return result.stripeClientSecret;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to initialize payment. Please try again later.';
      // Don't overwrite the step if we already transitioned to confirming-payment
      if (step !== 'confirming-payment') {
        setCheckoutError(errorMessage);
        setStep('error');
      }
      throw error;
    }
  }, [organizationId, companyId, step]);

  return {
    step,
    paymentConfirmationTimedOut,
    checkoutError,
    openTierSelection,
    selectPro,
    closeTierSelection,
    closeCheckout,
    closeSuccess,
    closeError,
    fetchUpgradeClientSecret,
  };
}
